// src/hooks/useCart.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

const SESSION_KEY = 'onebox_cart_session';
const ORDER_KEY   = 'onebox_last_order';
const PHONE_KEY   = 'onebox_customer_phone';

export const useCart = (tenantId) => {
  const [cartItems,       setCartItems]       = useState([]);
  const [sessionId,       setSessionId]       = useState(null);
  const [cartSessionDbId, setCartSessionDbId] = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [isOpen,          setIsOpen]          = useState(false);

  const [activeOrder, setActiveOrder] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(ORDER_KEY)) || null; }
    catch { return null; }
  });

  const [savedPhone, setSavedPhone] = useState(
    () => sessionStorage.getItem(PHONE_KEY) || ''
  );

  const isMountedRef  = useRef(true);
  const clearTimerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    };
  }, []);

  // ── Get or create session ID ──────────────────────────────────────
  useEffect(() => {
    if (!tenantId) return;
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, sid);
    }
    setSessionId(sid);
  }, [tenantId]);

  // ── Load cart from Supabase ───────────────────────────────────────
  const loadCart = useCallback(async () => {
    if (!sessionId || !tenantId) return;
    try {
      let { data: session } = await supabase
        .from('cart_sessions')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('session_id', sessionId)
        .eq('status', 'active')
        .single();

      if (!session) {
        const { data: newSession } = await supabase
          .from('cart_sessions')
          .insert({ tenant_id: tenantId, session_id: sessionId })
          .select()
          .single();
        session = newSession;
      }

      if (!session) return;
      setCartSessionDbId(session.id);

      const { data: items } = await supabase
        .from('cart_items')
        .select(`
          *,
          menu_items (
            id, name, price, image_url, description,
            preparation_time, dietary_type
          )
        `)
        .eq('cart_session_id', session.id)
        .order('created_at');

      if (isMountedRef.current) setCartItems(items || []);
    } catch (err) {
      console.error('Cart load error:', err);
    }
  }, [sessionId, tenantId]);

  useEffect(() => { loadCart(); }, [loadCart]);

  // ── Persist active order to sessionStorage ────────────────────────
  useEffect(() => {
    if (activeOrder) {
      sessionStorage.setItem(ORDER_KEY, JSON.stringify(activeOrder));
    } else {
      sessionStorage.removeItem(ORDER_KEY);
    }
  }, [activeOrder]);

  // ── Add item ──────────────────────────────────────────────────────
  const addToCart = useCallback(async (
    menuItem,
    quantity     = 1,
    variant      = {},
    modifiers    = [],
    instructions = ''
  ) => {
    if (!cartSessionDbId) return;
    setLoading(true);
    try {
      const existing = cartItems.find(ci =>
        ci.menu_item_id === menuItem.id &&
        JSON.stringify(ci.selected_variant) === JSON.stringify(variant)
      );

      if (existing) {
        await updateQuantity(existing.id, existing.quantity + quantity);
      } else {
        const unitPrice     = variant?.price || menuItem.price;
        const modifierTotal = modifiers.reduce((sum, m) => sum + (m.price || 0), 0);
        const finalPrice    = parseFloat(unitPrice) + modifierTotal;

        await supabase.from('cart_items').insert({
          cart_session_id:      cartSessionDbId,
          tenant_id:            tenantId,
          menu_item_id:         menuItem.id,
          quantity,
          unit_price:           finalPrice,
          total_price:          finalPrice * quantity,
          selected_variant:     variant,
          selected_modifiers:   modifiers,
          special_instructions: instructions,
        });
      }
      await loadCart();
    } catch (err) {
      console.error('Add to cart error:', err);
    } finally {
      setLoading(false);
    }
  }, [cartSessionDbId, cartItems, tenantId, loadCart]);

  // ── Update quantity ───────────────────────────────────────────────
  const updateQuantity = useCallback(async (cartItemId, newQty) => {
    if (newQty <= 0) { await removeItem(cartItemId); return; }
    const item = cartItems.find(ci => ci.id === cartItemId);
    if (!item) return;
    await supabase.from('cart_items').update({
      quantity:    newQty,
      total_price: item.unit_price * newQty,
    }).eq('id', cartItemId);
    await loadCart();
  }, [cartItems, loadCart]);

  // ── Remove item ───────────────────────────────────────────────────
  const removeItem = useCallback(async (cartItemId) => {
    await supabase.from('cart_items').delete().eq('id', cartItemId);
    await loadCart();
  }, [loadCart]);

  // ── Clear cart ────────────────────────────────────────────────────
  const clearCart = useCallback(async () => {
    if (!cartSessionDbId) return;
    await supabase.from('cart_items').delete().eq('cart_session_id', cartSessionDbId);
    await loadCart();
  }, [cartSessionDbId, loadCart]);

  // ── Auto-clear helper ─────────────────────────────────────────────
  const scheduleAutoClear = useCallback(() => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setActiveOrder(null);
        sessionStorage.removeItem(ORDER_KEY);
      }
    }, 8000);
  }, []);

  // ── Place order ───────────────────────────────────────────────────
  const placeOrder = useCallback(async (customerInfo) => {
    if (!cartSessionDbId || cartItems.length === 0) return null;
    setLoading(true);
    try {
      const subtotal = cartItems.reduce((sum, item) => sum + parseFloat(item.total_price), 0);
      const tax      = subtotal * 0.05;
      const total    = subtotal + tax;

      const itemsSnapshot = cartItems.map(ci => ({
        menu_item_id: ci.menu_item_id,
        name:         ci.menu_items?.name,
        quantity:     ci.quantity,
        unit_price:   ci.unit_price,
        total_price:  ci.total_price,
        variant:      ci.selected_variant,
        modifiers:    ci.selected_modifiers,
        instructions: ci.special_instructions,
        image_url:    ci.menu_items?.image_url,
      }));

      const { data: order, error } = await supabase
        .from('orders')
        .insert({
          tenant_id:       tenantId,
          order_number:    '',
          cart_session_id: cartSessionDbId,
          customer_name:   customerInfo.name,
          customer_phone:  customerInfo.phone,
          customer_email:  customerInfo.email       || null,
          table_number:    customerInfo.tableNumber  || null,
          order_type:      customerInfo.orderType    || 'dine_in',
          notes:           customerInfo.notes        || null,
          subtotal:        subtotal.toFixed(2),
          tax_amount:      tax.toFixed(2),
          total_amount:    total.toFixed(2),
          items_snapshot:  itemsSnapshot,
          status:          'pending',
          payment_status:  'pending',
        })
        .select()
        .single();

      if (error) throw error;

      await supabase
        .from('cart_sessions')
        .update({
          status:         'checkout',
          customer_name:  customerInfo.name,
          customer_phone: customerInfo.phone,
          order_type:     customerInfo.orderType || 'dine_in',
        })
        .eq('id', cartSessionDbId);

      // ── Fetch ALL active orders for this phone (incl. new one) ───
      const { data: allActiveOrders } = await supabase
        .from('orders')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('customer_phone', customerInfo.phone.trim())
        .not('status', 'in', '("served","cancelled")')
        .order('created_at', { ascending: false });

      const ordersToTrack = allActiveOrders?.length ? allActiveOrders : [order];

      // Piggyback the full list onto activeOrder so OrderTracker
      // can seed all order IDs for polling from the start
      const enrichedOrder = {
        ...order,
        _allActiveOrders: ordersToTrack,
      };

      setActiveOrder(enrichedOrder);
      setSavedPhone(customerInfo.phone);
      sessionStorage.setItem(PHONE_KEY, customerInfo.phone);

      // Fresh cart session for next order
      localStorage.removeItem(SESSION_KEY);
      setCartItems([]);
      setIsOpen(false);

      return order;
    } catch (err) {
      console.error('Place order error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [cartSessionDbId, cartItems, tenantId]);

  // ─────────────────────────────────────────────────────────────────
  // LOOKUP ORDER
  // Signature: lookupOrderByPhone(phone, orderId, multi)
  //
  //   lookupOrderByPhone('9876543210', null, false) → single latest order
  //   lookupOrderByPhone('9876543210', null, true)  → ALL active orders (array)
  //   lookupOrderByPhone(null, 'uuid-here')         → poll single order by ID
  // ─────────────────────────────────────────────────────────────────
  const lookupOrderByPhone = useCallback(async (
    phone,
    orderId,
    multi = false
  ) => {
    try {

      // ── 1. Poll by order ID ───────────────────────────────────────
      if (orderId) {
        const { data, error } = await supabase
          .from('orders')
          .select('*')
          .eq('id', orderId)
          .single();

        if (error || !data) return null;

        // Sync activeOrder if this is the same order
        if (isMountedRef.current) {
          setActiveOrder(prev => {
            if (!prev || prev.id !== data.id) return prev;

            const wasActive = !['served', 'cancelled'].includes(prev.status);
            const isNowDone = ['served', 'cancelled'].includes(data.status);

            if (wasActive && isNowDone) scheduleAutoClear();

            // Preserve _allActiveOrders so it survives polling updates
            return { ...prev, ...data, _allActiveOrders: prev._allActiveOrders };
          });
        }

        return data;
      }

      // ── 2. Manual lookup by phone ─────────────────────────────────
      if (phone && tenantId) {
        const baseQuery = supabase
          .from('orders')
          .select('*')
          .eq('tenant_id', tenantId)
          .eq('customer_phone', phone.trim())
          .not('status', 'in', '("served","cancelled")')
          .order('created_at', { ascending: false });

        if (multi) {
          const { data, error } = await baseQuery;
          if (error || !data?.length) return null;
          return data; // array
        } else {
          const { data, error } = await baseQuery.limit(1).single();
          if (error || !data) return null;
          return data;
        }
      }

      return null;
    } catch (_) {
      return null;
    }
  }, [tenantId, scheduleAutoClear]);

  // ── Computed values ───────────────────────────────────────────────
  const totalItems = cartItems.reduce((sum, ci) => sum + ci.quantity, 0);
  const subtotal   = cartItems.reduce((sum, ci) => sum + parseFloat(ci.total_price), 0);
  const tax        = subtotal * 0.05;
  const total      = subtotal + tax;

  return {
    cartItems,
    totalItems,
    subtotal,
    tax,
    total,
    loading,
    isOpen,
    setIsOpen,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    placeOrder,
    loadCart,
    activeOrder,
    setActiveOrder,
    savedPhone,
    lookupOrderByPhone,
  };
};
